import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useForm, useWatch } from "react-hook-form";
import styled from "@emotion/styled";
import { RHFTextField } from "@rentablo/components";
import { Banner } from "@rentablo/components";
import { LoadingButton } from "@rentablo/components";
import { useTwoLogins } from "@api/v1/hooks/useTwoLogins";
import { preventNonNumericInput } from "@helpers";
import { Link, Typography } from "@mui/material";
import { TwoFAFormData } from "../types";

const FormStyled = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(6)};
`;

const LoadingButtonStyled = styled(LoadingButton)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

interface Props {
  twoFAFormData: TwoFAFormData;
  onLogin: (access_token: string) => void;
  goBack: () => void;
}

interface FormFields {
  code: string;
}

export const TwoFAForm = ({ onLogin, twoFAFormData, goBack }: Props) => {
  const { t } = useTranslation();
  const { loginAsync, isFetching } = useTwoLogins();
  const [showError, setShowError] = useState<boolean>(false);

  const { handleSubmit, control } = useForm<FormFields>({
    defaultValues: { code: "" },
  });

  const code = useWatch({ control, name: "code" });

  const onFormSubmit = async (data: FormFields) => {
    if (data.code) {
      try {
        setShowError(false);
        const result = await loginAsync({
          grant_type: "two_factor_authentication",
          username: twoFAFormData.email,
          password: twoFAFormData.password,
          mfa_challenge_code: Number(data.code),
          mfa_secret: twoFAFormData.secret,
          rememberMe: twoFAFormData.rememberMe,
        });

        if (result.access_token) {
          onLogin(result.access_token!);
        }
      } catch (e) {
        setShowError(true);
      }
    }
  };

  return (
    <FormStyled onSubmit={handleSubmit(onFormSubmit)} data-testid="2fa-form">
      {showError && (
        <Banner
          type="error"
          text={
            <Trans
              i18nKey="loginPage.twoFACodeError"
              components={{
                linkText: (
                  <Typography
                    component={Link}
                    onClick={goBack}
                    variant="body3"
                    lineHeight="20px"
                  />
                ),
              }}
            />
          }
          displayDefaultIcon
        />
      )}
      <RHFTextField
        label={t("loginPage.form.twoFACodeLabel")}
        name="code"
        type="number"
        fullWidth
        control={control}
        disabled={isFetching}
        testId="2fa-code-field"
        onBeforeInput={preventNonNumericInput}
      />
      <LoadingButtonStyled
        fullWidth
        type="submit"
        variant="primary"
        data-testid="2fa-submit-button"
        isFetching={isFetching}
        disabled={!code}
      >
        {t("loginPage.form.twoFASubmitButton")}
      </LoadingButtonStyled>
    </FormStyled>
  );
};
